import { type ComponentProps, useMemo } from "react"
import cn from "@/v2-ui/utils/utils.cn"
import Icon from "@/v2-ui/icon"
import Dot from "@/v2-backoffice-client/components/dot/Dot"
import UptimeLatencyBadge from "@/v2-backoffice-client/uptime/UptimeLatencyBadge"
import UptimeOverviewSparkline from "@/v2-backoffice-client/uptime/UptimeOverviewSparkline"
import UptimeOverviewRibbon, {
  buildRibbonCells,
  incidentCountToCellVariant,
  uptimePercentToCellVariant,
} from "@/v2-backoffice-client/uptime/UptimeOverviewRibbon"
import UptimeStatusBreakdown from "@/v2-backoffice-client/uptime/UptimeStatusBreakdown"
import {
  aggregateDailyDriftDetections,
  UPTIME_SPARK_DAYS,
  UPTIME_SPARK_HOURS,
} from "@/v2-backoffice-client/uptime/uptime.charts.utils"
import {
  formatRelativeTime,
  formatUptimePercent,
} from "@/v2-backoffice-client/uptime/uptime.utils"
import type { UptimeOverviewFragment } from "@/v2-backoffice-client/uptime/__types__/uptime.overview.fragment"

// Minimal structural target type accepted by UptimeOverview. Accepts
// both UptimeTargetFragment (full 90d history) and UptimeInlineFragment
// (no history). When history fields are absent, chart aggregators return
// null-filled series and sparklines render empty.
type OverviewTarget = {
  IsMuted: boolean
  LatestStatus: string
  IsSlowOutlier: boolean
  HasMetadataDrift: boolean
  HasCountDrift: boolean
  DailyHistory?: Array<{
    Date: string
    TotalChecks: number
    DownChecks: number
  }>
  LatencyHourlyHistory?: Array<{
    BucketStart: string
    AvgDurationMs?: number | null
  }>
  DriftDetectedAt?: string | null
}

export type UptimeOverviewProps = ComponentProps<"section"> & {
  overview: UptimeOverviewFragment | null | undefined
  isLoading?: boolean
  // Per-target list. Used purely to compute background sparklines for
  // each KPI card (aggregated daily uptime, hourly latency, daily
  // incident counts, daily drift detections). Optional -- the cards
  // still render without sparklines if the parent has not yet
  // resolved the targets list.
  targets?: OverviewTarget[]
  // driftCount is computed client-side from the per-target list
  // because the overview row has no schema-level drift aggregate.
  // Optional to keep this card-strip usable in places that haven't
  // wired the count yet (it just hides the drift card).
  driftCount?: number
  onIncidentsClick?: () => void
  onDriftClick?: () => void
}

function UptimeOverview(props: UptimeOverviewProps) {
  const {
    overview,
    isLoading,
    targets,
    driftCount = 0,
    onIncidentsClick,
    onDriftClick,
    className,
    ...sectionProps
  } = props

  const dailyUptime = useMemo(
    () => fleetDailyUptimeSeries(overview?.FleetDailyHistory),
    [overview],
  )
  const hourlyLatency = useMemo(
    () => fleetHourlyLatencySeries(overview?.FleetHourlyLatency),
    [overview],
  )
  const dailyIncidents = useMemo(
    () => fleetDailyIncidentSeries(overview?.FleetDailyHistory),
    [overview],
  )
  const dailyDrifts = useMemo(
    () => aggregateDailyDriftDetections(targets ?? []),
    [targets],
  )

  // Daily ribbons -- a row of 30 colour-coded cells per card, each
  // cell = one day. The Uptime card uses a green→amber→red ramp on
  // the daily fleet uptime; the Active Incidents card uses a red
  // intensity ramp on the daily incident count. Replaces the old
  // sparkline behind those cards because the sparkline collapsed to
  // an invisible flat line on quiet days. Latency keeps the
  // sparkline -- it actually has a meaningful curve.
  const uptimeRibbon = useMemo(
    () =>
      buildRibbonCells({
        series: dailyUptime,
        days: UPTIME_SPARK_DAYS,
        toVariant: uptimePercentToCellVariant,
        formatTooltip: (v, date) =>
          v == null
            ? `${date}: no data`
            : `${date}: ${formatUptimePercent(v)} up`,
      }),
    [dailyUptime],
  )
  const incidentRibbon = useMemo(
    () =>
      buildRibbonCells({
        series: dailyIncidents,
        days: UPTIME_SPARK_DAYS,
        toVariant: incidentCountToCellVariant,
        formatTooltip: (v, date) => {
          const n = v ?? 0
          if (n === 0) return `${date}: no incidents`
          return `${date}: ${n} ${n === 1 ? "incident" : "incidents"}`
        },
      }),
    [dailyIncidents],
  )

  if (!overview) {
    return (
      <section
        className={cn("uptime-overview-grid", className)}
        aria-label="Overview"
        {...sectionProps}
      >
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className="uptime-overview-card">
            <div className="uptime-overview-card-label">
              {isLoading ? "Loading…" : "—"}
            </div>
            <div className="uptime-overview-card-value text-muted-foreground">
              —
            </div>
          </div>
        ))}
      </section>
    )
  }

  // Drift counts as "warning" severity for the at-a-glance status
  // colour: it doesn't take a service down, but it deserves yellow.
  const statusVariant = computeStatusVariant(overview, driftCount)
  const cardStatusClass = {
    ok: "uptime-overview-card-status-ok",
    warning: "uptime-overview-card-status-warning",
    critical: "uptime-overview-card-status-critical",
  }[statusVariant]

  const statusDot: "success-1" | "destructive" | "warning" | "muted" = (() => {
    if (statusVariant === "ok") return "success-1"
    if (statusVariant === "critical") return "destructive"
    return "warning"
  })()

  // Every count we read off `overview` here describes the **active**
  // (non-muted) fleet -- the backend rolls everything up against the
  // mute filter (see UptimeOverview / Get_Backoffice_UptimeOverview).
  // `MutedCount` is the only field that talks about the silenced
  // set, and we surface it as a separate annotation below so the
  // operator can still see "+5 muted" without it skewing any of the
  // headline numbers / percentages / sparklines.
  const downCount = overview.DownCount
  const incidentHeadline = overview.ActiveIncidentCount
  const mutedCount = overview.MutedCount

  const statusHeadline = (() => {
    if (downCount > 0) return `${downCount} down`
    if (overview.SkippedCount > 0) return `${overview.SkippedCount} skipped`
    if (driftCount > 0) return `${driftCount} drifting`
    return "All operational"
  })()

  return (
    <section
      className={cn("uptime-overview-grid", className)}
      aria-label="Overview"
      {...sectionProps}
    >
      <div
        className={cn(
          "uptime-overview-card",
          cardStatusClass,
          statusVariant === "critical" && "uptime-overview-card-pulse",
        )}
      >
        <div className="uptime-overview-card-label">
          <Dot variant={statusDot} /> Overall status
        </div>
        <div className="uptime-overview-card-value">{statusHeadline}</div>
        <div className="uptime-overview-card-sub">
          {overview.UpCount}/{overview.TotalTargets} up ·{" "}
          <span
            className={
              downCount > 0 ? "text-destructive font-medium" : undefined
            }
          >
            {downCount} down
          </span>
          {" · "}
          {overview.SkippedCount} skipped
          {driftCount > 0 && (
            <>
              {" · "}
              <span className="text-violet-500 font-medium">
                {driftCount} drifting
              </span>
            </>
          )}
          {mutedCount > 0 && (
            <>
              {" · "}
              <span className="text-muted-foreground/80">
                +{mutedCount} muted
              </span>
            </>
          )}
        </div>
        {/*
          Single composition bar at the bottom of the card (in place
          of the trend sparkline that other cards use) -- the
          operator already gets the trend from the Uptime card; what
          they're missing here is "what's the *mix* right now: how
          much is up vs down vs slow vs drifting?". One thin bar with
          a wrapped legend underneath.
        */}
        <UptimeStatusBreakdown
          targets={targets ?? []}
          className="uptime-overview-card-breakdown"
        />
      </div>

      <div className="uptime-overview-card uptime-overview-card-with-ribbon">
        <UptimeOverviewRibbon cells={uptimeRibbon} />
        <div className="uptime-overview-card-label">Uptime · 24 h</div>
        <div className="uptime-overview-card-value">
          {formatUptimePercent(overview.UptimePercent24h)}
        </div>
        <div className="uptime-overview-card-sub">
          7 d {formatUptimePercent(overview.UptimePercent7d)} · 30 d{" "}
          {formatUptimePercent(overview.UptimePercent30d)} · 90 d{" "}
          {formatUptimePercent(overview.UptimePercent90d)}
        </div>
      </div>

      <div className="uptime-overview-card">
        <UptimeOverviewSparkline series={hourlyLatency} variant="primary" />
        <div className="uptime-overview-card-label">Latency · 24 h</div>
        <div className="uptime-overview-card-value">
          <UptimeLatencyBadge
            currentMs={overview.P95DurationMs24h ?? null}
            variant="normal"
            tooltip="Global p95 latency across every up probe in the last 24h"
          />{" "}
          <span className="text-xs text-muted-foreground font-normal ml-1">
            p95
          </span>
        </div>
        <div className="uptime-overview-card-sub">
          p50{" "}
          {overview.P50DurationMs24h == null
            ? "—"
            : `${overview.P50DurationMs24h} ms`}{" "}
          · avg{" "}
          {overview.AvgDurationMs24h == null
            ? "—"
            : `${overview.AvgDurationMs24h} ms`}
        </div>
      </div>

      <button
        type="button"
        onClick={onIncidentsClick}
        className={cn(
          "uptime-overview-card uptime-overview-card-clickable text-left uptime-overview-card-with-ribbon",
          incidentHeadline > 0 &&
            "uptime-overview-card-status-critical uptime-overview-card-pulse",
        )}
        aria-label="Jump to active incidents"
      >
        <UptimeOverviewRibbon cells={incidentRibbon} />
        <div className="uptime-overview-card-label">
          <Icon
            src="triangle-warning"
            className={
              incidentHeadline > 0
                ? "text-destructive"
                : "text-muted-foreground"
            }
          />
          Active incidents
        </div>
        <div className="uptime-overview-card-value">{incidentHeadline}</div>
        <div className="uptime-overview-card-sub">
          {overview.IncidentsStarted24h} started in 24 h ·{" "}
          {overview.IncidentsStarted7d} in 7 d · {overview.IncidentsStarted30d}{" "}
          in 30 d
          {overview.LastIncidentAt && (
            <>
              <br />
              last incident {formatRelativeTime(overview.LastIncidentAt)}
            </>
          )}
        </div>
      </button>

      <button
        type="button"
        onClick={onDriftClick}
        className={cn(
          "uptime-overview-card uptime-overview-card-clickable text-left",
          driftCount > 0 && "uptime-overview-card-status-drift",
        )}
        aria-label="Jump to configuration drift"
      >
        <UptimeOverviewSparkline
          series={dailyDrifts}
          variant="violet"
          shape="bar"
        />
        <div className="uptime-overview-card-label">
          <Icon
            src="swap"
            className={
              driftCount > 0 ? "text-violet-500" : "text-muted-foreground"
            }
          />
          Configuration drift
        </div>
        <div className="uptime-overview-card-value">
          {driftCount}
          {driftCount > 0 && (
            <span className="text-xs text-muted-foreground font-normal ml-2">
              layers
            </span>
          )}
        </div>
        <div className="uptime-overview-card-sub">
          {driftCount > 0
            ? "upstream metadata diverged from baseline"
            : "all baselines match live metadata"}
        </div>
      </button>
    </section>
  )
}

// ---------------------------------------------------------------------------
// Fleet-level chart helpers
// These derive the KPI-card series directly from the pre-aggregated
// FleetDailyHistory / FleetHourlyLatency arrays returned by the overview
// query, avoiding the need to fan out across per-target history arrays.
// ---------------------------------------------------------------------------

type FleetDailyEntry = { Date: string; TotalChecks: number; DownChecks: number }
type FleetHourlyEntry = { BucketStart: string; AvgDurationMs?: number | null }

/** 30-day daily uptime % series (most-recent day last). */
function fleetDailyUptimeSeries(
  history: FleetDailyEntry[] | null | undefined,
): Array<number | null> {
  const map = new Map<string, FleetDailyEntry>()
  for (const d of history ?? []) map.set(d.Date, d)
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  const out: Array<number | null> = []
  for (let i = UPTIME_SPARK_DAYS - 1; i >= 0; i--) {
    const date = new Date(today)
    date.setDate(today.getDate() - i)
    const key = date.toISOString().slice(0, 10)
    const d = map.get(key)
    out.push(
      d && d.TotalChecks > 0
        ? (100 * (d.TotalChecks - d.DownChecks)) / d.TotalChecks
        : null,
    )
  }
  return out
}

/** 30-day daily incident count series (days with DownChecks > 0). */
function fleetDailyIncidentSeries(
  history: FleetDailyEntry[] | null | undefined,
): Array<number | null> {
  const map = new Map<string, FleetDailyEntry>()
  for (const d of history ?? []) map.set(d.Date, d)
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  const out: Array<number | null> = []
  for (let i = UPTIME_SPARK_DAYS - 1; i >= 0; i--) {
    const date = new Date(today)
    date.setDate(today.getDate() - i)
    const key = date.toISOString().slice(0, 10)
    const d = map.get(key)
    out.push(d && d.DownChecks > 0 ? d.DownChecks : 0)
  }
  return out
}

/** 24-hour hourly latency series (oldest hour first). */
function fleetHourlyLatencySeries(
  history: FleetHourlyEntry[] | null | undefined,
): Array<number | null> {
  const map = new Map<number, number>()
  const now = new Date()
  now.setMinutes(0, 0, 0)
  for (const h of history ?? []) {
    if (h.AvgDurationMs == null || h.AvgDurationMs <= 0) continue
    const bucket = new Date(h.BucketStart).getTime()
    const offset = Math.round((now.getTime() - bucket) / 3_600_000)
    if (offset >= 0 && offset < UPTIME_SPARK_HOURS)
      map.set(offset, h.AvgDurationMs)
  }
  const out: Array<number | null> = []
  for (let i = UPTIME_SPARK_HOURS - 1; i >= 0; i--) {
    out.push(map.get(i) ?? null)
  }
  return out
}

function computeStatusVariant(
  overview: UptimeOverviewFragment,
  driftCount = 0,
): "ok" | "warning" | "critical" {
  // Every count on `overview` already excludes muted targets (see
  // the comment on UptimeOverviewFragment / the Go model), so we can
  // branch directly without any "actionable vs raw" gymnastics.
  if (overview.DownCount > 0 || overview.ActiveIncidentCount > 0) {
    return "critical"
  }
  if (overview.SkippedCount > 0 || driftCount > 0) {
    return "warning"
  }
  return "ok"
}

export default UptimeOverview
